import { useFormik } from "formik";
import { Alert, Col, Form, InputGroup, Row, Tab, Table } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import * as yup from "yup";
import Select from "react-select";
import { amountTypes, formatDate, formatForQty } from "../../../utils/helpers";
import CurrencyCustomInput from "../../utils/CurrencyCustomInput";
import NoTableItem from "../../utils/NoTableItem";
import { useMemo, useState } from "react";
import currency from "currency.js";
import { addMonths, addQuarters, addYears } from "date-fns";
import DateTime from "react-datetime";
import { addDays } from "date-fns";
import useDebounce from "../../../utils/hooks";
import { cloneDeep, divide, isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import { useAuth } from "../../../hooks/useAuth";
import ConfirmDialog from "../../ConfirmDialogue";
import queryString from "query-string";
import CachedIcon from "mdi-react/CachedIcon";
import { Popover } from "react-tiny-popover";
import ExpensesSelector from "../../utils/ExpensesSelector";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import AddAccounts from "../../modals/AddAccounts";
import { Link } from "react-router-dom";
import PlayIcon from "mdi-react/PlayIcon";
import PauseIcon from "mdi-react/PauseIcon";

// 12, 4, 2, 1
const cycleOptions = [
  { label: "Monthly", value: "Monthly", divideBy: 12, humanText: "per month" },
  {
    label: "Quaterly",
    value: "Quaterly",
    divideBy: 4,
    humanText: "per quarter",
  },
  {
    label: "Bi-annually",
    value: "Bi-annually",
    divideBy: 2,
    humanText: "per half year",
  },
  { label: "Annually", value: "Annually", divideBy: 1, humanText: "per year" },
];

export default function DepreciationModal({
  showDepreciationModal = true,
  setShowDepreciationModal,
}) {
  const { backendUrl } = useAuth();
  const [showCreateNewAccountModal, setShowCreateNewAccountModal] = useState(
    false
  );
  const [
    showCreateNewExpenseAccountModal,
    setShowCreateNewExpenseAccountModal,
  ] = useState(false);

  // GET old Data
  const getBatches = async () => {
    let response = await fetch(
      `${backendUrl}/api/warehouse/asset-depreciation-projection?${queryString.stringify(
        {
          Bar_Code: showDepreciationModal.Bar_Code,
          W_ID: showDepreciationModal.W_ID,
          Item_Name: showDepreciationModal.Item_Name,
        }
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    // FOR EDITING
    if (data?.depreciation) {
      formik.setValues({
        id: data.depreciation.id,
        cycle: data.depreciation.cycle || "Monthly",
        type: data.depreciation.type || "Percentage",
        fixedAmount: data.depreciation.fixedAmount || 0,
        percentage: data.depreciation.percentage || null,
        depreciationPercentage:
          data.depreciation.depreciationPercentage || null,
        Post_Date: data.depreciation?.Post_Date
          ? new Date(data.depreciation.Post_Date.split("T")[0])
          : new Date(),
        // Start_Date: null,
        accumulatedAccountID: data.depreciation?.accumulatedAccountID || "",
        accumulatedAccountDescription:
          data.depreciation?.accumulatedAccountDescription || "",
        accumulatedAccountType: data.depreciation?.accumulatedAccountType || "",
        expenseAccountID: data.depreciation?.expenseAccountID || "",
        expenseAccountDescription:
          data.depreciation?.expenseAccountDescription || "",
        expenseAccountType: data.depreciation?.expenseAccountType || "",
        isPaused: data.depreciation?.isPaused || false,
      });
    }
    return data;
  };

  const { isLoading, data, refetch } = useQuery(
    ["PROJECTED_DEPRECIATION"],
    () => getBatches(),
    {
      enabled: !!showDepreciationModal,
    }
  );

  const formik = useFormik({
    initialValues: {
      id: null,
      cycle: "Monthly",
      type: "Percentage",
      fixedAmount: 0,
      percentage: null,
      depreciationPercentage: null,
      // ---
      Post_Date: new Date(),
      // Start_Date: null,
      // ---
      accumulatedAccountID: "",
      accumulatedAccountDescription: "",
      accumulatedAccountType: "",
      // ---
      expenseAccountID: "",
      expenseAccountDescription: "",
      expenseAccountType: "",

      isPaused: false,
    },
    validationSchema: yup.object().shape({
      cycle: yup.string().required(),
    }),
    onSubmit: async values => {
      // VALIDATIONS
      if (showDepreciationModal.UnitCost <= 0) {
        return toast.error("Unit cost must be greater than zero");
      }
      if (showDepreciationModal.Quantity <= 0) {
        return toast.error("Quantity must be greater than zero");
      }

      // DEPRECATED PER ITEM NOW
      // if (isEmpty(values.Start_Date)) {
      //   return toast.error("Start Date is required");
      // }

      if (!values.accumulatedAccountID) {
        return toast.error("Select accumulated depreciation account");
      }

      if (!values.expenseAccountID) {
        return toast.error("Select expense account");
      }

      if (!values.depreciationPercentage) {
        return toast.error("Enter depreciation percentage");
      }
      //---

      if (
        await ConfirmDialog({
          title: "Save Depreciation Setup",
          description: `Are you sure, you want to submit, Future projection will be based on this setup?`,
        })
      ) {
        values.item = showDepreciationModal; //item is saved here for reference in backend
        values.percentageValue = percentageValue;

        postAssetDepreciationMutation.mutate(values);
      }
    },
  });

  const percentageValue = useMemo(() => {
    const totalUnitCost = currency(showDepreciationModal.UnitCost, {
      precision: 4,
    }).multiply(showDepreciationModal.Quantity).value;

    return currency(formik.values.percentage, {
      precision: 4,
    })
      .divide(100)
      .multiply(totalUnitCost).value;
  }, [formik.values.percentage, showDepreciationModal.UnitCost]);

  const addAssetDepreciation = async payload => {
    let response = await fetch(
      `${backendUrl}/api/warehouse/add-asset-depreciation`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(payload),
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const res = await response.json();
    return res;
  };

  const postAssetDepreciationMutation = useMutation(
    payload => addAssetDepreciation(payload),
    {
      onSuccess: ({ message, data }) => {
        toast.success(message);
        //  formik.resetForm();
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  // GA ===============================================================
  const [showJournalPopover, setShowJournalPopover] = useState(false);
  const handleSelectedAccount = account => {
    // console.log(account);
    formik.setFieldValue("accumulatedAccountID", account?.AccountID);
    formik.setFieldValue("accumulatedAccountDescription", account?.Description);
    formik.setFieldValue("accumulatedAccountType", account?.Type);
    setShowJournalPopover(false);
  };

  // GA ===============================================================
  // EXPENSE SECOND LEG
  const [showJournalExpensePopover, setShowJournalExpensePopover] = useState(
    false
  );
  const handleSelectedExpenseAccount = account => {
    // console.log(account);
    formik.setFieldValue("expenseAccountID", account?.AccountID);
    formik.setFieldValue("expenseAccountDescription", account?.Description);
    formik.setFieldValue("expenseAccountType", account?.Type);
    setShowJournalExpensePopover(false);
  };

  const calcutePercentage = (depreciationPercentage = 0, cycle) => {
    // console.log(cycle);
    const divideBy = cycle
      ? cycleOptions.find(el => el.value === cycle)?.divideBy
      : 1;

    const percentage = currency(depreciationPercentage, {
      precision: 2,
    }).divide(divideBy).value;

    formik.setFieldValue("percentage", percentage || 0);
    // console.log({ depreciationPercentage, divideBy, percentage });
  };

  const handleDepreciationPercentageChange = e => {
    const depreciationPercentage = e.target.value;
    formik.setFieldValue("depreciationPercentage", depreciationPercentage);
    calcutePercentage(depreciationPercentage, formik.values.cycle);
  };

  const handleCycleChange = selected => {
    // console.log(selected);
    formik.setFieldValue("cycle", selected.value);
    calcutePercentage(formik.values.depreciationPercentage, selected.value);
  };

  return (
    <>
      <Modal
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        animation={false}
        enforceFocus={false}
        show={showDepreciationModal}
        onHide={() => {
          setShowDepreciationModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Depreciation {"  "}{" "}
            <Button
              variant=""
              className="text-primary"
              onClick={() => refetch()}
            >
              <CachedIcon />
            </Button>
            {"  "}{" "}
            {data?.depreciation?.id && (
              <Button
                size="sm"
                variant="light-blue"
                disabled={postAssetDepreciationMutation.isLoading}
                onClick={() => {
                  formik.setFieldValue("isPaused", !formik.values?.isPaused);
                }}
              >
                <span className="d-flex gap-3 text-nowrap align-items-center">
                  {formik.values?.isPaused ? (
                    <span>
                      <PlayIcon /> Resume
                    </span>
                  ) : (
                    <span>
                      <PauseIcon /> Pause
                    </span>
                  )}
                </span>
              </Button>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {data?.depreciation?.wasSetBy === "DEPRECIATION_SETUP" ? (
            <Alert variant="warning">
              <p>
                This asset has been set up for depreciation in the
                <Link
                  to="/depreciations"
                  className="fw-5 text-decoration-underline"
                  target="_blank"
                >
                  {" "}
                  depreciations{" "}
                </Link>
                set up module. Setting it here will override the previous setup.
              </p>
              {data.depreciation.title && (
                <span>{data.depreciation.title}</span>
              )}{" "}
              /{" "}
              {data.depreciation.usage && (
                <span>{data.depreciation.usage}</span>
              )}
            </Alert>
          ) : null}

          <Row>
            <Col>
              <p>
                <b>Item Code:</b> {showDepreciationModal?.Bar_Code}
              </p>

              <p>
                <b>Item Name:</b> {showDepreciationModal?.Item_Name}
              </p>

              <p>
                <b>Start Date:</b>{" "}
                {data?.depreciation
                  ? formatDate(data?.depreciation?.Start_Date)
                  : "..."}
              </p>
            </Col>

            <Col>
              <p>
                <b>Unit Cost:</b>{" "}
                {currency(showDepreciationModal.UnitCost, {
                  symbol: "",
                }).format()}
              </p>

              <p>
                <b>Quantity:</b>{" "}
                {currency(showDepreciationModal.Quantity, {
                  symbol: "",
                  format: formatForQty,
                }).format()}
              </p>

              <p>
                <b>Total Unit Cost:</b>{" "}
                {currency(showDepreciationModal.UnitCost, {
                  symbol: "",
                })
                  .multiply(showDepreciationModal.Quantity)
                  .format()}
              </p>
            </Col>
          </Row>

          <hr />

          <Form noValidate onSubmit={formik.handleSubmit}>
            <Row>
              <Col>
                <Form.Group className="mb-3 pb-2">
                  <Form.Label className="mb-1">
                    Depreciation Percentage (%)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="depreciationPercentage"
                    value={formik.values.depreciationPercentage}
                    onChange={handleDepreciationPercentageChange}
                    placeholder="0.0"
                  />{" "}
                </Form.Group>
                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Cycle</Form.Label>
                      <Select
                        classNamePrefix={"form-select"}
                        options={cycleOptions}
                        value={cycleOptions.find(
                          el => el.value === formik.values.cycle
                        )}
                        onChange={handleCycleChange}
                        isDisabled={true}
                      />
                      {formik.touched.cycle && !!formik.errors.cycle ? (
                        <span className="text-danger mt-2">
                          {formik.errors.cycle}
                        </span>
                      ) : null}
                    </Form.Group>{" "}
                  </Col>

                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Type</Form.Label>
                      <Select
                        classNamePrefix={"form-select"}
                        options={amountTypes}
                        value={amountTypes?.find(
                          el => el.value === formik.values?.type
                        )}
                        onChange={selected =>
                          formik.setFieldValue("type", selected.value)
                        }
                        isDisabled={true}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {formik.values?.type === "Percentage" ? (
                  <Row>
                    <Col>
                      <Form.Group className="mb-3 pb-2">
                        <Form.Label className="mb-1">
                          Percentage (%){" "}
                          {
                            cycleOptions.find(
                              el => el.value === formik.values.cycle
                            )?.humanText
                          }{" "}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="percentage"
                          value={formik.values.percentage}
                          onChange={formik.handleChange}
                          readOnly
                          placeholder="0.0"
                        />{" "}
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3 pb-2">
                        <Form.Label className="mb-1">Amount</Form.Label>
                        <CurrencyCustomInput
                          //   name="amount"
                          placeholder="0.00"
                          value={percentageValue}
                          onValueChange={(value, name) => {}}
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                ) : formik.values?.type === "Fixed" ? (
                  <Form.Group className="mb-3 pb-2">
                    <Form.Label className="mb-1">Fixed Amount</Form.Label>
                    <CurrencyCustomInput
                      name="fixedAmount"
                      placeholder="0.00"
                      value={formik.values.fixedAmount}
                      onValueChange={(value, name) => {
                        formik.setFieldValue(name, value);
                      }}
                    />{" "}
                  </Form.Group>
                ) : null}
                <hr />
                <h2 className="mb-1 pb-1 h5">Account Postings</h2>
                <Form.Group className="mb-3">
                  <Form.Label>Accumulated Depreciation</Form.Label>
                  <div className="d-flex gap-3">
                    <InputGroup hasValidation>
                      <Form.Control
                        placeholder="Select Account"
                        value={`${formik.values?.accumulatedAccountID ||
                          "..."} / ${formik.values
                          ?.accumulatedAccountDescription || "..."} / ${formik
                          .values?.accumulatedAccountType || "..."}`}
                        onChange={() => {}}
                        readOnly
                        isInvalid={
                          formik.touched.accumulatedAccountID &&
                          !!formik.errors.accumulatedAccountID
                        }
                      />

                      <Popover
                        isOpen={showJournalPopover}
                        onClickOutside={() => setShowJournalPopover(false)}
                        content={() => (
                          <ExpensesSelector
                            handleSelectedExpense={handleSelectedAccount}
                            usage={"chart of accounts"}
                            Type="NON-CURRENT ASSETS"
                          />
                        )}
                        position="bottom"
                      >
                        <InputGroup.Text
                          onClick={() => setShowJournalPopover(true)}
                          className="p-cursor"
                        >
                          <MagnifyIcon />
                        </InputGroup.Text>
                      </Popover>
                    </InputGroup>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setShowCreateNewAccountModal(true)}
                      className="text-nowrap"
                    >
                      + Create New
                    </Button>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Depreciation Expense</Form.Label>
                  <div className="d-flex gap-3">
                    <InputGroup hasValidation>
                      <Form.Control
                        placeholder="Select Account"
                        value={`${formik.values?.expenseAccountID ||
                          "..."} / ${formik.values?.expenseAccountDescription ||
                          "..."} / ${formik.values?.expenseAccountType ||
                          "..."}`}
                        onChange={() => {}}
                        readOnly
                        isInvalid={
                          formik.touched.expenseAccountID &&
                          !!formik.errors.expenseAccountID
                        }
                      />

                      <Popover
                        isOpen={showJournalExpensePopover}
                        onClickOutside={() =>
                          setShowJournalExpensePopover(false)
                        }
                        content={() => (
                          <ExpensesSelector
                            handleSelectedExpense={handleSelectedExpenseAccount}
                            usage={"chart of accounts"}
                            Type="EXPENSE"
                          />
                        )}
                        position="bottom"
                      >
                        <InputGroup.Text
                          onClick={() => setShowJournalExpensePopover(true)}
                          className="p-cursor"
                        >
                          <MagnifyIcon />
                        </InputGroup.Text>
                      </Popover>
                    </InputGroup>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setShowCreateNewExpenseAccountModal(true)}
                      className="text-nowrap"
                    >
                      + Create New
                    </Button>
                  </div>
                </Form.Group>
                <hr />
                {/*   <Form.Group className="mb-3">
                  <>
                    {data?.depreciation?.id ? (
                      <Form.Label>
                        Old Start Date:{" "}
                        {data?.depreciation?.Start_Date
                          ? new Date(data?.depreciation?.Start_Date)
                              .toISOString()
                              .slice(0, 10)
                          : null}
                      </Form.Label>
                    ) : null}
                    <br />
                  </>

                  <br />
                  {data?.depreciation?.id ? (
                    <>
                      <Form.Label>New Start Date</Form.Label>
                      <br />
                      <span className="text-danger">
                        Be careful, This will change the projection
                      </span>
                    </>
                  ) : (
                    <>
                      <Form.Label>Start Date</Form.Label>
                    </>
                  )}
                  <br />

                  <DateTime
                    timeFormat={false}
                    closeOnSelect={true}
                    closeOnClickOutside={true}
                    dateFormat="MMM DD, YYYY"
                    inputProps={{
                      className: `date-input form-control`,
                      placeholder: "Select date",
                      readOnly: true,
                    }}
                    value={formik.values.Start_Date}
                    onChange={date => {
                      formik.setFieldValue("Start_Date", date, true);
                    }}
                  />
                </Form.Group> */}
                <Form.Group className="mb-3">
                  <Form.Label>Post Date</Form.Label>
                  <DateTime
                    timeFormat={false}
                    closeOnSelect={true}
                    closeOnClickOutside={true}
                    dateFormat="MMM DD, YYYY"
                    inputProps={{
                      className: `date-input form-control`,
                      placeholder: "Select date",
                      readOnly: true,
                    }}
                    value={formik.values.Post_Date}
                    onChange={date => {
                      formik.setFieldValue("Post_Date", date, true);
                    }}
                  />
                </Form.Group>{" "}
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="" onClick={() => setShowDepreciationModal(false)}>
            Close
          </Button>
          <Button
            onClick={() => formik.submitForm()}
            className="px-4"
            disabled={postAssetDepreciationMutation.isLoading}
          >
            {postAssetDepreciationMutation.isLoading ? "Please wait.." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {showCreateNewAccountModal && (
        <AddAccounts
          show={showCreateNewAccountModal}
          onHide={() => setShowCreateNewAccountModal(false)}
          refetch={() => refetch()}
          type={"NON-CURRENT ASSETS"}
          accountCreated={handleSelectedAccount}
        />
      )}

      {showCreateNewExpenseAccountModal && (
        <AddAccounts
          show={showCreateNewExpenseAccountModal}
          onHide={() => setShowCreateNewExpenseAccountModal(false)}
          refetch={() => refetch()}
          type={"EXPENSE"}
          accountCreated={handleSelectedExpenseAccount}
        />
      )}
    </>
  );
}
